import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ConfirmModal from "../../components/ConfirmModal";
import { Select } from "../../components/Form/Input/select";
import Label from "../../components/Form/Label";
import { FormGroup } from "../../components/Form/styles";
import { ScreenHeader } from "../../components/SubHeader";
import { useToast } from "../../components/Toast";
import { useAppState } from "../../contexts/App";
import { getPreviousPath } from "../../hooks/PreviousPath";
import { api, ApiSiteResponse, BusinessCard, BusinessCardData } from "../../services/api";
import { CardFields } from "./components/CardFields";
import { CardPreview } from "./components/CardPreview";
import { CardSkeleton, FormSkeleton, WalletsSkeleton } from "./components/Loading";
import { Wallets } from "./components/Wallets";
import {
  ActionButton,
  Block,
  Card,
  Container,
  EmptyCall,
  Note,
  Sample,
  SaveRow,
  Section,
  Showcase,
  Studio,
  TwoFields,
} from "./styles";

const prefix = "pages.businessCard";

// The Keepo picked on this screen last time (on this browser): where it starts when none is being edited
const LAST_PICKED_KEY = "Keepo.businessCardSiteId";
const lastPicked = {
  get: () => {
    try {
      return window.localStorage.getItem(LAST_PICKED_KEY);
    } catch {
      return null;
    }
  },
  set: (siteId: string) => {
    try {
      window.localStorage.setItem(LAST_PICKED_KEY, siteId);
    } catch {
      // a browser that keeps nothing: it starts at the first Keepo
    }
  },
};

const FIELDS = ["name", "role"] as const;

/** The card as the form shows it: each field as it was kept (even empty, when cleared), else what the Keepo has. */
const filledFrom = ({ card, defaults }: BusinessCardData): BusinessCard =>
  Object.fromEntries(FIELDS.map(key => [key, key in card ? card[key] || "" : defaults[key] || ""]));

/**
 * What goes to the API: a field the owner touched (or that was already kept) as they wrote it, "" if they cleared it.
 * A field they never touched is left out, so it keeps following the Keepo (its welcome, its field of work).
 */
const writtenFrom = (card: BusinessCard, data: BusinessCardData): BusinessCard => {
  const shown = filledFrom(data);
  return Object.fromEntries(
    FIELDS.flatMap(key => {
      const value = card[key]?.trim() || "";
      return key in data.card || value !== (shown[key] || "").trim() ? [[key, value]] : [];
    })
  );
};

/** A card that is nobody's, dimmed, to show what a published Keepo gets. */
const SAMPLE_COLORS = { background: "#2A2D37", foreground: "#FFFFFF" };

/**
 * The business card of a Keepo: drawn from it (name, photo, the colors of its theme, its address and QR), what
 * goes on it, and the wallets it can go to. A person with more than one Keepo picks whose card.
 */
const BusinessCardPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { appState } = useAppState();

  const [keepos, setKeepos] = useState<ApiSiteResponse[]>();
  // whether the person has a Keepo that isn't published yet (a draft): the empty screen asks to publish it
  const [hasDraft, setHasDraft] = useState(false);
  const [siteId, setSiteId] = useState<string>();
  // the Keepo on the screen now, for an answer that comes after another one was picked
  const current = useRef<string>();
  current.current = siteId;
  // Where it was opened from, once: picking another Keepo here doesn't change it
  const [cameFromKeepo] = useState(() => getPreviousPath() === "/keepo");
  const [data, setData] = useState<BusinessCardData>();
  const [hasLoadError, setLoadError] = useState(false);
  // what goes on the card, filled with what the Keepo has; drawn as it is written, saved with the button
  const [card, setCard] = useState<BusinessCard>({});
  // how the last save of the card went
  const [saving, setSaving] = useState<"saving" | "error">();
  // the card as it is in the form now, for an answer that comes after the person kept writing
  const written = useRef(card);
  written.current = card;
  // the Keepo asked for while there are changes not saved: it waits for the person's answer
  const [asked, setAsked] = useState<string>();

  // the Keepos of the person; the card starts at the one being edited
  useEffect(() => {
    api
      .getSites()
      .then(all => {
        // only a published Keepo has a card: its address, its QR, its page as it is live
        const sites = all.filter(keepo => !!keepo.site);
        setKeepos(sites);
        setHasDraft(all.length > 0);
        // the Keepo being edited, else the one picked here last time, else the first
        const known = (id?: string | null) => sites.find(keepo => keepo.siteId === id);
        setSiteId((known(appState.editingSiteId) || known(lastPicked.get()) || sites[0])?.siteId);
      })
      .catch(() => setLoadError(true));
  }, [appState.editingSiteId]);

  useEffect(() => {
    if (!siteId) return;
    let active = true;
    setData(undefined);
    setLoadError(false);
    api
      .getBusinessCard(siteId)
      .then(loaded => {
        if (!active) return;
        setData(loaded);
        setCard(filledFrom(loaded));
        setSaving(undefined);
      })
      .catch(() => active && setLoadError(true));
    return () => {
      active = false;
    };
  }, [siteId]);

  const titleOf = (keepo: ApiSiteResponse) => keepo.site?.title || keepo.draft?.title || keepo.siteId;

  // what was written and not saved yet
  const isDirty = !!data && FIELDS.some(key => (card[key] || "") !== (filledFrom(data)[key] || ""));

  // Leaving the page (closing the tab, reloading) with it
  useEffect(() => {
    if (!isDirty) return;
    const warn = (event: BeforeUnloadEvent) => event.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [isDirty]);

  // A Keepo picked here is the one of this screen, and where it starts next time: the one being edited in the rest of
  // the panel stays as it is (its content, plan and the rest are loaded for it, not for this one)
  const choose = (picked: string) => {
    setSiteId(picked);
    lastPicked.set(picked);
  };
  const pick = (picked: string) => (isDirty ? setAsked(picked) : choose(picked));

  // the card as it is written, before it is saved
  const preview = data && {
    ...data.preview,
    name: card.name?.trim() || data.defaults.name,
    // the name always has one; what the person cleared stays off the card
    role: card.role?.trim() || null,
  };

  // the card is drawn again only when what it shows changes
  const previewKey = JSON.stringify(preview ?? null);

  // until the card is in, its shape pulsing (there's nothing to show when no Keepo is published)
  const isLoading = !data && !hasLoadError && keepos?.length !== 0;
  const cardPreview = useMemo(
    () => (preview ? <CardPreview preview={preview} /> : isLoading ? <CardSkeleton /> : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- drawn again only when what it shows changes
    [previewKey, isLoading]
  );

  const save = async () => {
    if (!siteId || !data || saving === "saving") return;
    setSaving("saving");
    const sent = card;
    try {
      const saved = await api.saveBusinessCard(siteId, writtenFrom(sent, data));
      toast.success(t(`${prefix}.form.saved.card`));
      // another Keepo was picked meanwhile: its card stays on the screen
      if (current.current !== siteId) return;
      setData(saved);
      // the form is how the API keeps it now, unless the person kept writing while it was saving: that stays
      if (written.current === sent) setCard(filledFrom(saved));
      setSaving(undefined);
    } catch {
      if (current.current === siteId) setSaving("error");
    }
  };

  const change = (changed: BusinessCard) => {
    setCard(changed);
    setSaving(current => (current === "saving" ? current : undefined));
  };

  // whose card, for a person with more than one Keepo
  const keepoPicker = keepos && keepos.length > 1 && (
    <FormGroup>
      <Select id="keepo" value={siteId} onChange={event => pick(event.target.value)}>
        {keepos.map(keepo => (
          <option key={keepo.siteId} value={keepo.siteId}>
            {titleOf(keepo)}
          </option>
        ))}
      </Select>
      <Label htmlFor="keepo" text={t(`${prefix}.keepo`)} />
    </FormGroup>
  );

  // the button that saves, with how the last save went
  const saveRow = (
    <SaveRow>
      {saving === "error" && <Note role="alert">{t(`${prefix}.form.saveError`)}</Note>}
      <ActionButton type="button" $loading={saving === "saving"} aria-busy={saving === "saving"} onClick={save}>
        {t(`${prefix}.form.save.card`)}
      </ActionButton>
    </SaveRow>
  );

  return (
    <Container>
      {/* Opened from the Keepo's screen (its shortcut): a way back to it above the title, as its other screens */}
      <ScreenHeader
        title={t(`${prefix}.title`)}
        back={cameFromKeepo ? { label: t("components.subHeader.keepo"), to: "/keepo" } : undefined}
        description={t(`${prefix}.description`)}
      />

      {/* No published Keepo: a sample of the card, dimmed, and the way to have one (create a Keepo, or publish it) */}
      {keepos && keepos.length === 0 && (
        <Section>
          <Showcase>
            <Sample>
              <CardPreview
                preview={{
                  ...SAMPLE_COLORS,
                  name: t(`${prefix}.empty.sampleName`),
                  role: t(`${prefix}.empty.sampleRole`),
                  url: t(`${prefix}.empty.sampleUrl`),
                  photo: null,
                }}
              />
            </Sample>
            <EmptyCall>
              <p>{t(`${prefix}.empty.${hasDraft ? "publish" : "create"}`)}</p>
              <ActionButton
                type="button"
                onClick={() => (hasDraft ? navigate("/sites") : navigate("/sites", { state: { createNew: true } }))}>
                {t(`${prefix}.empty.${hasDraft ? "publishButton" : "createButton"}`)}
              </ActionButton>
            </EmptyCall>
          </Showcase>
        </Section>
      )}

      {/* A card that didn't load: the picker stays, to go to another Keepo */}
      {hasLoadError && (
        <Section>
          {keepoPicker}
          <Note role="alert">{t(`${prefix}.loadError`)}</Note>
        </Section>
      )}

      {(isLoading || data) && (
        <Section>
          {/* The card is the page: in a showcase with its wallets, and under it what goes on it, to personalize */}
          <Studio>
            <Showcase>
              {cardPreview}
              {data && siteId ? <Wallets siteId={siteId} data={data} /> : <WalletsSkeleton />}
            </Showcase>

            {isLoading ? (
              <FormSkeleton />
            ) : (
              <Card>
                <Block aria-labelledby="business-card-fields">
                  <div className="head">
                    <div>
                      <h3 id="business-card-fields">{t(`${prefix}.form.title`)}</h3>
                      <p>{t(`${prefix}.fromKeepo`)}</p>
                    </div>
                  </div>

                  <div className="fields">
                    {keepoPicker}
                    <TwoFields>
                      <CardFields card={card} onChange={change} />
                    </TwoFields>
                    {saveRow}
                  </div>
                </Block>
              </Card>
            )}
          </Studio>
        </Section>
      )}

      {asked && (
        <ConfirmModal
          type="DANGER"
          title={t(`${prefix}.unsaved.title`)}
          descriptions={[t(`${prefix}.unsaved.description`)]}
          confirmButtonTitle={t(`${prefix}.unsaved.confirm`)}
          cancelTitle={t(`${prefix}.unsaved.cancel`)}
          onConfirm={() => {
            choose(asked);
            setAsked(undefined);
          }}
          onCancel={() => setAsked(undefined)}
        />
      )}
    </Container>
  );
};

export default BusinessCardPage;
